import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Pencil, Trash2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import { UserAvatar } from "@/components/UserAvatar";

interface ServiceCategory {
  id: string;
  name: string;
}

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  budget: number | null;
  pricing_type: string;
  created_at: string;
  service_category_id: string | null;
  user_id: string;
  service_category?: ServiceCategory | null;
}

// Category icons map matching DiscoverServices
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Web Development": Briefcase,
  "Design": Briefcase,
  "Writing": Briefcase,
  "Marketing and Sales": Briefcase,
  "Game Development": Briefcase,
  "Tutoring": Briefcase,
  "Photography": Briefcase,
  "Music": Briefcase,
  "Language": Briefcase,
  "Health": Briefcase,
  "Fashion": Briefcase,
  "Business": Briefcase,
  "Arts": Briefcase,
  "Engineering and CAD": Briefcase,
  "Other": Briefcase
};

// Category colors matching DiscoverServices
const categoryColors: Record<string, string> = {
  "Web Development": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Game Development": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Health": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

const MyRequestsList = () => {
  const { user } = useAuth();
  const { getUserProfile } = useUserProfiles();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});

  const loadRequests = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      // First get the service requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('service_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (requestsError) {
        console.error('Error loading requests:', requestsError);
        toast.error("Failed to load your requests");
        return;
      }

      // Then get the categories separately (without the icon column that doesn't exist)
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('service_categories')
        .select('id, name');

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
      }

      // Join the data manually
      const requestsWithCategories = requestsData?.map(request => ({
        ...request,
        service_category: request.service_category_id 
          ? categoriesData?.find(cat => cat.id === request.service_category_id) || null
          : null
      })) || [];

      setRequests(requestsWithCategories);
      
      // Load user profiles
      if (requestsWithCategories.length > 0) {
        const userIds = [...new Set(requestsWithCategories.map(request => request.user_id))];
        const profiles: Record<string, any> = {};
        
        for (const userId of userIds) {
          try {
            const profile = await getUserProfile(userId);
            profiles[userId] = profile;
          } catch (error) {
            console.error(`Error loading profile for ${userId}:`, error);
          }
        }
        
        setUserProfiles(profiles);
      }
    } catch (error) {
      console.error("Error loading requests:", error);
      toast.error("Failed to load your requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadRequests();
    }
  }, [user]);

  const handleEditRequest = (requestId: string) => {
    navigate(`/edit-request/${requestId}`);
  };

  const handleDeleteConfirmation = (requestId: string) => {
    setRequestToDelete(requestId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmedDelete = async () => {
    if (!requestToDelete) return;
    
    try {
      const { error } = await supabase
        .from('service_requests')
        .delete()
        .eq('id', requestToDelete);

      if (error) {
        console.error('Error deleting request:', error);
        toast.error("Failed to delete request");
        return;
      }

      toast.success("Request deleted successfully");
      // Refresh the requests list
      loadRequests();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error("Failed to delete request");
    } finally {
      // Reset delete state
      setIsDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };

  const formatBudget = (budget: number | null) => {
    if (!budget) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(budget);
  };

  const shortenDescription = (description: string, maxLength: number = 150) => {
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };

  const getPricingTypeLabel = (pricingType: string) => {
    return pricingType === 'hourly' ? 'Hourly Rate' : 'Fixed Price';
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-12 h-12 border-4 border-anyskill-purple border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-lg text-gray-600">Loading your requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end mb-4">
        <Button 
          className="bg-anyskill-purple hover:bg-anyskill-purple/90"
          onClick={() => navigate("/new-request")}
        >
          + Post New Request
        </Button>
      </div>
      {requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h2 className="text-xl font-medium text-muted-foreground">You haven't created any service requests yet</h2>
          <p className="text-muted-foreground mt-2">Create your first request to find the services you need</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {requests.map((request) => {
          const categoryName = request.service_category?.name || "Uncategorized";
          const userProfile = userProfiles[request.user_id] || { 
            name: "Unknown User", 
            avatarInitials: "UN",
            avatarUrl: undefined 
          };
          const IconComponent = categoryIcons[categoryName] || Briefcase;
          const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
          
          return (
            <Card key={request.id} className="overflow-hidden hover:shadow-md transition-shadow relative border-2 border-blue-200">
              {/* Request Badge */}
              <div className="absolute top-2 left-2 z-10">
                <Badge variant="secondary" className="bg-blue-500 text-white text-xs font-medium px-2 py-1">
                  REQUEST
                </Badge>
              </div>

              {/* Category Icon Area - matching service card design */}
              <div className="aspect-[3/1] bg-gray-100 relative overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  <div className={`${colorClass} p-2 rounded-full`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                </div>
              </div>
              
              <CardContent className="p-4">
                {/* User Profile Section - matching service card design */}
                <div className="flex items-center mb-3">
                  <UserAvatar
                    userId={request.user_id}
                    avatarUrl={userProfile.avatarUrl}
                    fallback={userProfile.avatarInitials}
                    size="sm"
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600 truncate">{userProfile.name}</span>
                </div>
                
                {/* Title - matching service card design */}
                <CardTitle className="text-base mb-3 line-clamp-2 leading-tight">{request.title}</CardTitle>
                
                {/* Description - matching service card design */}
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {shortenDescription(request.description || "No description provided")}
                </p>
                
                {/* Pricing and Date Section - matching service card design */}
                <div className="flex justify-between items-end">
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      {request.budget && (
                        <span className="font-semibold text-base">{formatBudget(request.budget)}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Posted {new Date(request.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  
                  {/* Action Buttons - matching service card design */}
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                      onClick={() => handleEditRequest(request.id)}
                    >
                      <Pencil size={14} />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDeleteConfirmation(request.id)}
                      className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                    >
                      <Trash2 size={14} />
                      <span className="hidden sm:inline">Delete</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        </div>
      )}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this request?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your service request
              and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyRequestsList;
